import * as React from 'react';
import camelCase from 'lodash/camelCase';

import { IMakeErrorMsgID, makeErrorMsgID, useHoverControl } from '../../Utils';
import TooltipLink from '../../Tooltip/TooltipLink';
import { WithTooltipContainer } from '../../Tooltip/TooltipStyles';
import { IInputFieldProps } from '../TextInput/InputTextField';
import { IDisabledProps, IErrorMsgs, IInputMaxWidth, IReadOnlyProps, IUnitTestAttributes } from '../common';
import {
  FormError,
  FormGroup,
  FormHelperTxt,
  FormLabel,
} from '../FormStyles';
import { HoverContext } from '../Context/hoverContext';

import { InputControlContext } from './InputControlContext';

interface IInputControlProps extends IErrorMsgs, IDisabledProps, IInputFieldProps, IReadOnlyProps, IInputMaxWidth {
  isOptional: boolean;
  inputProps?: (React.InputHTMLAttributes<HTMLInputElement>
    | React.SelectHTMLAttributes<HTMLSelectElement>
    | React.TextareaHTMLAttributes<HTMLTextAreaElement>) & IUnitTestAttributes & {
    optionalInputFieldText?: string;
  };
}

const InputControl: React.FC<IInputControlProps> = (
  {
    children,
    errorMsgs,
    helperText,
    isDisabled,
    isOptional,
    isReadOnly,
    label,
    tooltipProps,
    inputProps,
    maxWidth,
  }) => {
  const errorMsgControls: IMakeErrorMsgID | null = errorMsgs ? makeErrorMsgID(errorMsgs, label) : null;
  const errorMsgIds: string = errorMsgControls && errorMsgControls.errorMsgIds ? errorMsgControls.errorMsgIds.join(' ') : '';
  const helperTextId: string = helperText ? camelCase(`${label} helper text`) : '';
  const hasError: boolean = !!errorMsgControls;
  const hasTooltip: boolean = !!tooltipProps;
  const inputId: string = camelCase(`${label}`);
  const tooltipId: string = tooltipProps ? camelCase(`${inputId} tooltip`) : '';
  const TooltipContainer = hasTooltip ? WithTooltipContainer : React.Fragment;
  // below eliminates https://reactjs.org/warnings/unknown-prop.html
  const marginControlTop: 'top' | {} = TooltipContainer === React.Fragment ? {} : { marginControl: 'top' };
  const marginControlBottom: 'bottom' | {} = TooltipContainer === React.Fragment ? {} : { marginControl: 'bottom' };
  const { isHovered, toggleHover } = useHoverControl();
  const optionalInputFieldText = inputProps && inputProps.optionalInputFieldText ? inputProps.optionalInputFieldText : '(Optional)';

  return (
    <HoverContext.Provider value={{ isHovered, toggleHover }}>
      <FormGroup maxWidth={maxWidth}>
        <TooltipContainer {...marginControlBottom}>
          <FormLabel
            error={hasError}
            htmlFor={inputId}
            isDisabled={isDisabled}
            isReadOnly={isReadOnly}
            isHovered={isHovered}
          >
            {label}{' '}{isOptional && optionalInputFieldText}
          </FormLabel>

          {tooltipProps && tooltipProps.location === 'label' && (
            <TooltipLink
              tooltipId={tooltipId}
              srText={tooltipProps.srText}
              paneProps={tooltipProps.paneProps}
            >
              {tooltipProps.paneText}
            </TooltipLink>
          )}
        </TooltipContainer>

        <InputControlContext.Provider value={{ error: hasError, ariaDescribedby: `${helperTextId} ${errorMsgIds}`.trim(), }}>
          {children}
        </InputControlContext.Provider>

        {helperText && (
          <TooltipContainer {...marginControlTop}>
            <FormHelperTxt
              id={helperTextId}
              isDisabled={isDisabled}
              isReadOnly={isReadOnly}
              isHovered={isHovered}
              error={hasError}
            >
              {helperText}
            </FormHelperTxt>

            {tooltipProps && tooltipProps.location === 'helperText' && (
              <TooltipLink
                tooltipId={tooltipId}
                srText={tooltipProps.srText}
                paneProps={tooltipProps.paneProps}
              >
                {tooltipProps.paneText}
              </TooltipLink>
            )}
          </TooltipContainer>
        )}

        {hasError && errorMsgControls && (
          errorMsgControls.errorMessages.map((msg, index) => (
            <FormError id={errorMsgControls.errorMsgIds[index]} key={msg}>
              {msg}
            </FormError>
          )
        ))}
      </FormGroup>
    </HoverContext.Provider>
  );
};

export default InputControl;
